import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { SIGNAL_THRESHOLDS } from "contracts/report/health";
import type { ExecutiveSummaryModule } from "../../enums/scan";
import { EXECUTIVE_SUMMARY_MODULE } from "../../enums/scan";
import { pluralize } from "../../ui/format";
import { metricLabel } from "../website/issueModel";
import { overviewScanAvailability } from "./scanAvailability";
import { contentFitnessHeadline } from "./scanModel";
import {
  type SummaryPriorityFamily,
  summaryPriorityFamilies,
} from "./summaryPriorityFamilies";

type ExecutiveSummaryPriority = {
  title: string;
  detail: string;
  action: string;
  href: string;
};

type ExecutiveSummaryViewModel = {
  headline: string;
  summary: string[];
  priorities: ExecutiveSummaryPriority[];
  changes: string[];
  generatedAt: Date | null;
  sourceReferences: Array<{
    module: ExecutiveSummaryModule;
    state: string;
  }>;
};

export function buildExecutiveSummary(
  data: OverviewResponse,
  websiteId: string,
): ExecutiveSummaryViewModel {
  const priorityFamilies = summaryPriorityFamilies(data, 3);
  const scanState = overviewScanAvailability(data);
  return {
    headline: executiveHeadline(data),
    summary:
      contentFitnessHeadline(data).score === null
        ? [scanState.detail]
        : [scanState.detail, executiveBody(data)],
    priorities: priorityFamilies.map((family) =>
      priorityFromFamily(family, websiteId),
    ),
    changes: movementSummary(data),
    generatedAt: data.lastRun?.finishedAt ?? data.signal.createdAt,
    sourceReferences: [
      { module: EXECUTIVE_SUMMARY_MODULE.SCAN, state: scanState.label },
      { module: EXECUTIVE_SUMMARY_MODULE.MAP, state: "No map published" },
      { module: EXECUTIVE_SUMMARY_MODULE.BRIEF, state: "No brief created" },
      {
        module: EXECUTIVE_SUMMARY_MODULE.GOVERN,
        state: "No governance cycle active",
      },
    ],
  };
}

function executiveHeadline(data: OverviewResponse): string {
  const score = contentFitnessHeadline(data).score;
  if (score === null) {
    return overviewScanAvailability(data).label;
  }
  if (score >= SIGNAL_THRESHOLDS.good) {
    return "Protect a strong estate and close the remaining leaks.";
  }
  if (score >= SIGNAL_THRESHOLDS.watch) {
    return "The estate is usable, but priority paths need attention.";
  }
  return "Priority content paths need executive attention.";
}

function executiveBody(data: OverviewResponse): string {
  const headline = contentFitnessHeadline(data);
  const priorityFamilies = summaryPriorityFamilies(data, 3);
  const issueFocus =
    priorityFamilies.length > 0
      ? priorityFamilies.map((family) => family.title).join("; ")
      : "the current open findings";
  const movement =
    headline.scoreDelta === null
      ? "Movement against the previous scored run is available only through the stored trend."
      : headline.scoreDelta > 0
        ? "Content Fitness has improved since the previous scored run."
        : headline.scoreDelta < 0
          ? "Content Fitness has declined since the previous scored run."
          : "Content Fitness is unchanged since the previous scored run.";
  if (headline.score === null) {
    return `Priority attention is concentrated in ${issueFocus}. Start with the highest-priority findings in Scan.`;
  }
  return `Priority attention is concentrated in ${issueFocus}. ${movement}`;
}

function priorityFromFamily(
  family: SummaryPriorityFamily,
  websiteId: string,
): ExecutiveSummaryPriority {
  return {
    title: family.title,
    detail: `${family.dimension} / ${family.scope} / ${family.severity} severity`,
    action: family.action,
    href: `/w/${websiteId}/scan/issues?issue=${family.representative.id}`,
  };
}

function movementSummary(data: OverviewResponse): string[] {
  const changes: string[] = [];
  const scoreDelta = contentFitnessHeadline(data).scoreDelta;
  if (scoreDelta !== null) {
    changes.push(scoreMovementCopy(scoreDelta));
  }
  if (
    data.runSummary?.newIssues !== null &&
    data.runSummary?.newIssues !== undefined
  ) {
    changes.push(
      `${data.runSummary.newIssues} new ${pluralize(data.runSummary.newIssues, "issue", "issues")} recorded in the completed scored run.`,
    );
  }
  if (
    data.runSummary?.resolvedIssues !== null &&
    data.runSummary?.resolvedIssues !== undefined
  ) {
    changes.push(
      `${data.runSummary.resolvedIssues} ${pluralize(data.runSummary.resolvedIssues, "issue", "issues")} verified fixed in the completed scored run.`,
    );
  }
  for (const change of data.changes.slice(0, 2)) {
    changes.push(
      `${metricLabel(change.metricKey)} ${formatSigned(change.delta)}: ${change.note}`,
    );
  }
  return changes.slice(0, 4);
}

export function formatScoreDelta(value: number | null): string {
  if (value === null) {
    return "";
  }
  if (value === 0) {
    return "No change";
  }
  return `${value > 0 ? "+" : ""}${value} ${pluralize(Math.abs(value), "point", "points")}`;
}

function formatSigned(value: number): string {
  if (value === 0) {
    return "0";
  }
  return `${value > 0 ? "+" : ""}${value}`;
}

function deltaVerb(value: number): string {
  if (value > 0) {
    return "increased by";
  }
  if (value < 0) {
    return "decreased by";
  }
  return "was unchanged at";
}

function scoreMovementCopy(value: number): string {
  if (value === 0) {
    return "Content Fitness was unchanged since the previous scored run.";
  }
  const magnitude = Math.abs(value);
  return `Content Fitness score ${deltaVerb(value)} ${magnitude} ${pluralize(magnitude, "point", "points")} since the previous scored run.`;
}
